// src/page/StamprallyListV2.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

const pad2 = (n) => String(n).padStart(2, '0');

const fmtDateTime = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
};

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ---- 判定（機能設計書ベース） ----
// 開催状況（開催期間）
const getHoldingStatus = (ev) => {
  const now = new Date();
  const start = toDate(ev?.eventOpenDate ?? ev?.holdingStartDate ?? ev?.startDate);
  const end = toDate(ev?.eventCloseDate ?? ev?.holdingEndDate ?? ev?.endDate);
  if (!start || !end) return { key: 'unknown', label: '-' };
  if (now < start) return { key: 'preparing', label: '準備中' };
  if (start <= now && now <= end) return { key: 'holding', label: '開催中' };
  return { key: 'ended', label: '終了' };
};

// 交換状況（特典交換期間）
const getExchangeStatus = (ev) => {
  const now = new Date();
  const start = toDate(ev?.exchangeStartDate ?? ev?.rewardExchangeStartDate);
  const end = toDate(ev?.exchangeEndDate ?? ev?.rewardExchangeEndDate);
  if (!start || !end) return { key: 'unknown', label: '-' };
  if (now < start) return { key: 'before', label: '交換前' };
  if (start <= now && now <= end) return { key: 'in', label: '交換中' };
  return { key: 'after', label: '交換終了' };
};

// 公開状態（下書き/公開/非公開）
const getPublishState = (ev) => {
  // 期待: publishState: 'draft'|'published'|'unpublished' をAPIが返す
  if (ev?.publishState) {
    if (ev.publishState === 'draft') return { key: 'draft', label: '下書き' };
    if (ev.publishState === 'published') return { key: 'published', label: '公開' };
    if (ev.publishState === 'unpublished') return { key: 'unpublished', label: '非公開' };
  }

  // フォールバック（既存っぽい値）
  // publishedFlag === false → 非公開
  if (ev?.publishedFlag === false) return { key: 'unpublished', label: '非公開' };

  // publishedStartDate が未来 → 下書き扱い（未公開相当）
  const ps = toDate(ev?.publishedStartDate);
  if (ps && ps > new Date()) return { key: 'draft', label: '下書き' };

  // それ以外 → 公開
  return { key: 'published', label: '公開' };
};

// 閲覧制限（なし / あり(人数)）
const getViewRestrictionLabel = (ev) => {
  // 期待: viewLimitFlag / viewLimitCount
  const flag = ev?.viewLimitFlag ?? ev?.viewRestrictedFlag ?? ev?.hasViewLimit;
  const cnt = ev?.viewLimitCount ?? ev?.viewRestrictedCount ?? ev?.viewLimitUsers;
  if (!flag) return 'なし';
  if (Number.isFinite(Number(cnt)) && Number(cnt) > 0) return `あり(${Number(cnt)})`;
  return 'あり';
};

// 達成条件（必要数/総数）
const getAchievementLabel = (ev) => {
  const need = ev?.requiredStampCount ?? ev?.requiredCount ?? ev?.needCount;
  const total = ev?.totalStampCount ?? ev?.totalCount ?? ev?.allCount;
  if (need == null || total == null) return '-';
  return `${need}/${total}`;
};

// 特典タイプ（応募/店舗交換/自動付与）
const getRewardTypeLabel = (ev) => {
  const v = ev?.rewardType ?? ev?.benefitType ?? ev?.prizeType;
  // 期待: 'apply'|'store'|'auto' or 0/1/2
  if (v === 'apply' || v === 0 || v === '0') return '応募型';
  if (v === 'store' || v === 1 || v === '1') return '店舗交換型';
  if (v === 'auto' || v === 2 || v === '2') return '自動付与型';
  return v ? String(v) : '-';
};

// 設定状況（OK / 要設定）
const getConfigStatusLabel = (ev) => {
  // 期待: configOk / hasConfigError / requiredFieldsMissingCount など
  if (ev?.configOk === true) return 'OK';
  if (ev?.configOk === false) return '要設定';
  if (ev?.hasConfigError === true) return '要設定';
  const missing = ev?.requiredFieldsMissingCount;
  if (Number.isFinite(Number(missing)) && Number(missing) > 0) return '要設定';
  return '-';
};

const badgeClass = (kind) => {
  // bootstrap想定
  if (kind === 'published') return 'badge bg-success-subtle text-success border border-success-subtle';
  if (kind === 'draft') return 'badge bg-warning-subtle text-warning border border-warning-subtle';
  if (kind === 'unpublished') return 'badge bg-secondary-subtle text-secondary border border-secondary-subtle';

  if (kind === 'holding') return 'badge bg-success-subtle text-success border border-success-subtle';
  if (kind === 'preparing') return 'badge bg-warning-subtle text-warning border border-warning-subtle';
  if (kind === 'ended') return 'badge bg-secondary-subtle text-secondary border border-secondary-subtle';

  if (kind === 'in') return 'badge bg-success-subtle text-success border border-success-subtle';
  if (kind === 'before') return 'badge bg-warning-subtle text-warning border border-warning-subtle';
  if (kind === 'after') return 'badge bg-secondary-subtle text-secondary border border-secondary-subtle';

  return 'badge bg-light text-body border';
};

export default function StamprallyListV2() {
  useAuthGuard();
  const navigate = useNavigate();

  const [sp, setSp] = useSearchParams();

  // ---- URL初期値 ----
  const initPage = Number(sp.get('page') || 1);
  const initSize = Number(sp.get('size') || 20);
  const initQ = sp.get('q') || '';

  // Filters (spec)
  const initPublish = sp.get('publish') || 'all'; // all|draft|published|unpublished
  const initHolding = sp.get('holding') || 'all'; // all|preparing|holding|ended
  const initExchange = sp.get('exchange') || 'all'; // all|before|in|after
  const initReward = sp.get('reward') || 'all'; // all|apply|store|auto
  const initView = sp.get('view') || 'all'; // all|none|limited
  const initFrom = sp.get('from') || ''; // yyyy-mm-dd
  const initTo = sp.get('to') || ''; // yyyy-mm-dd

  // Sort (spec: default updated desc)
  const initSortKey = sp.get('sort') || 'updated';
  const initSortDir = sp.get('dir') || 'desc';

  // ---- state ----
  const [keywordInput, setKeywordInput] = useState(initQ);
  const [searchKeyword, setSearchKeyword] = useState(initQ);

  const [publishState, setPublishState] = useState(initPublish);
  const [holdingState, setHoldingState] = useState(initHolding);
  const [exchangeState, setExchangeState] = useState(initExchange);
  const [rewardType, setRewardType] = useState(initReward);
  const [viewRestriction, setViewRestriction] = useState(initView);
  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);

  const [sort, setSort] = useState({ key: initSortKey, dir: initSortDir });
  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'updated' ? 'desc' : 'asc' }
    );

  const [page, setPage] = useState(initPage);
  const [pageSize, setPageSize] = useState(initSize);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  // バリデーションエラーモーダル用
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // ---- paging ----
  const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(pageCount, p + 1));

  // ---- URLへ反映（UI変化なし） ----
  useEffect(() => {
    const p = new URLSearchParams();
    if (page !== 1) p.set('page', String(page));
    if (pageSize !== 20) p.set('size', String(pageSize));
    if (searchKeyword) p.set('q', searchKeyword);

    if (publishState !== 'all') p.set('publish', publishState);
    if (holdingState !== 'all') p.set('holding', holdingState);
    if (exchangeState !== 'all') p.set('exchange', exchangeState);
    if (rewardType !== 'all') p.set('reward', rewardType);
    if (viewRestriction !== 'all') p.set('view', viewRestriction);
    if (dateFrom) p.set('from', dateFrom);
    if (dateTo) p.set('to', dateTo);

    if (sort.key) p.set('sort', sort.key);
    if (sort.dir) p.set('dir', sort.dir);

    setSp(p, { replace: true });
  }, [
    page,
    pageSize,
    searchKeyword,
    publishState,
    holdingState,
    exchangeState,
    rewardType,
    viewRestriction,
    dateFrom,
    dateTo,
    sort,
    setSp,
  ]);

  // ---- Fetch list (STAMPRALLY) ----
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('access_token');

        // NOTE:
        // 既存の /v1/stamprally を継続利用しつつ、
        // 仕様で増えた絞り込みは query param として送る（API側で対応する想定）。
        const url = new URL(`${API_BASE_URL}/v1/stamprallies`);
        url.searchParams.set('page', String(page));
        url.searchParams.set('size', String(pageSize));

        // sort: updated|open|close|publish (API側で map)
        url.searchParams.set('sort', sort.key);
        url.searchParams.set('dir', sort.dir);

        if (searchKeyword.trim() !== '') url.searchParams.set('q', searchKeyword.trim());

        // filters
        if (publishState !== 'all') url.searchParams.set('publish', publishState);
        if (holdingState !== 'all') url.searchParams.set('holding', holdingState);
        if (exchangeState !== 'all') url.searchParams.set('exchange', exchangeState);
        if (rewardType !== 'all') url.searchParams.set('reward', rewardType);
        if (viewRestriction !== 'all') url.searchParams.set('view', viewRestriction);
        if (dateFrom) url.searchParams.set('from', dateFrom);
        if (dateTo) url.searchParams.set('to', dateTo);

        const res = await fetch(url.toString(), {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.total) || 0);

        if (data.size && Number(data.size) !== pageSize) setPageSize(Number(data.size));
      } catch (e) {
        if (e?.name !== 'AbortError') setError('スタンプラリーの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [
    page,
    pageSize,
    searchKeyword,
    publishState,
    holdingState,
    exchangeState,
    rewardType,
    viewRestriction,
    dateFrom,
    dateTo,
    sort,
    reloadKey,
  ]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  // ---- Search submit/clear ----
  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchKeyword(keywordInput);
    setPage(1);
  };

  const handleClear = () => {
    setKeywordInput('');
    setSearchKeyword('');
    setPublishState('all');
    setHoldingState('all');
    setExchangeState('all');
    setRewardType('all');
    setViewRestriction('all');
    setDateFrom('');
    setDateTo('');
    setSort({ key: 'updated', dir: 'desc' });
    setPageSize(20);
    setPage(1);
  };

  // ---- CSV Export (list) ----
  const handleExportCsv = async () => {
    try {
      const token = localStorage.getItem('access_token') || '';
      const url = new URL(`${API_BASE_URL}/v1/stamprallies/export`);

      // carry over filters/sort (export ignores pagination)
      if (searchKeyword.trim() !== '') url.searchParams.set('q', searchKeyword.trim());
      if (publishState !== 'all') url.searchParams.set('publish', publishState);
      if (holdingState !== 'all') url.searchParams.set('holding', holdingState);
      if (exchangeState !== 'all') url.searchParams.set('exchange', exchangeState);
      if (rewardType !== 'all') url.searchParams.set('reward', rewardType);
      if (viewRestriction !== 'all') url.searchParams.set('view', viewRestriction);
      if (dateFrom) url.searchParams.set('from', dateFrom);
      if (dateTo) url.searchParams.set('to', dateTo);
      if (sort?.key) url.searchParams.set('sort', sort.key);
      if (sort?.dir) url.searchParams.set('dir', sort.dir);

      const res = await fetch(url.toString(), {
        headers: {
          Accept: 'text/csv',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const now = new Date();
      const ts = [
        now.getFullYear(),
        pad2(now.getMonth() + 1),
        pad2(now.getDate()),
        pad2(now.getHours()),
        pad2(now.getMinutes()),
        pad2(now.getSeconds()),
      ].join('');

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `スタンプラリー一覧-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error(e);
      setError('CSVのダウンロードに失敗しました');
      alert('CSVのダウンロードに失敗しました');
    }
  };

  // ---- 公開/非公開切替（確認ダイアログ付き） ----
  const handleTogglePublish = async (ev) => {
    const cur = getPublishState(ev);
    const nextKey = cur.key === 'published' ? 'unpublished' : 'published';
    const nextLabel = nextKey === 'published' ? '公開' : '非公開';

    const ok = window.confirm(
      `「${ev?.stamprallyName ?? ''}」を「${nextLabel}」に切り替えます。\n公開状態の変更はアプリに即時影響します。よろしいですか？`
    );
    if (!ok) return;

    try {
      const token = localStorage.getItem('access_token') || '';
      // NOTE: API仕様に合わせて変更してください
      // 例: PATCH /v1/stamprallies/{stamprallyId}/publish { publishState: 'published'|'unpublished' } など
      const url = new URL(`${API_BASE_URL}/v1/stamprallies/${ev.stamprallyId}/publish`);

      const res = await fetch(url.toString(), {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ publishState: nextKey }),
      });
      if (!res.ok) {
        // バリデーションエラーの詳細を取得
        const errorData = await res.json().catch(() => null);
        if (errorData?.error === 'validation error' && errorData?.details) {
          setValidationErrors(errorData.details);
          setValidationModalOpen(true);
          return;
        }
        throw new Error(errorData?.error || `HTTP ${res.status}`);
      }

      // 再取得
      setReloadKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      setError('公開状態の切替に失敗しました');
      alert('公開状態の切替に失敗しました');
    }
  };

  // ---- 行クリック（詳細/編集導線） ----
  const goDetail = (id) => navigate(`/stamprally/${id}/edit`);

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><a href="/stamprally">スタンプラリー管理</a></li>
          <li className="breadcrumb-item active">スタンプラリー一覧</li>
        </ol>
      </nav>

      <div className="mb-9">
        <div className="row g-3 mb-4">
          <div className="col-auto">
            <h2 className="mb-0">スタンプラリー一覧</h2>
          </div>
        </div>

        {/* 検索・絞り込み（上部検索エリア） */}
        <div className="mb-4">
          <form className="position-relative w-100" onSubmit={handleSubmit}>
            <div className="d-flex flex-wrap gap-3 align-items-end">
              {/* キーワード */}
              <div className="search-box">
                <label className="form-label mb-1 d-block">キーワード</label>
                <input
                  aria-label="Search"
                  className="form-control search-input search"
                  placeholder="ID / 名称 / 説明（部分一致）"
                  type="search"
                  name="keyword"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                />
                <span className="fas fa-search search-box-icon"></span>
              </div>

              {/* 公開状態 */}
              <div>
                <label className="form-label mb-1 d-block">公開状態</label>
                <select
                  className="form-select"
                  value={publishState}
                  onChange={(e) => { setPublishState(e.target.value); setPage(1); }}
                >
                  <option value="all">全て</option>
                  <option value="draft">下書き</option>
                  <option value="published">公開</option>
                  <option value="unpublished">非公開</option>
                </select>
              </div>

              {/* 開催状況 */}
              <div>
                <label className="form-label mb-1 d-block">開催状況（開催期間）</label>
                <select
                  className="form-select"
                  value={holdingState}
                  onChange={(e) => { setHoldingState(e.target.value); setPage(1); }}
                >
                  <option value="all">全て</option>
                  <option value="preparing">準備中</option>
                  <option value="holding">開催中</option>
                  <option value="ended">終了</option>
                </select>
              </div>

              {/* 交換状況 */}
              <div>
                <label className="form-label mb-1 d-block">交換状況（特典交換期間）</label>
                <select
                  className="form-select"
                  value={exchangeState}
                  onChange={(e) => { setExchangeState(e.target.value); setPage(1); }}
                >
                  <option value="all">全て</option>
                  <option value="before">交換前</option>
                  <option value="in">交換中</option>
                  <option value="after">交換終了</option>
                </select>
              </div>

              {/* 特典タイプ */}
              <div>
                <label className="form-label mb-1 d-block">特典タイプ</label>
                <select
                  className="form-select"
                  value={rewardType}
                  onChange={(e) => { setRewardType(e.target.value); setPage(1); }}
                >
                  <option value="all">全て</option>
                  <option value="apply">応募型</option>
                  <option value="store">店舗交換型</option>
                  {/* 自動付与型は今フェーズでは非表示
                  <option value="auto">自動付与型</option>
                  */}
                </select>
              </div>

              {/* 閲覧制限 */}
              <div>
                <label className="form-label mb-1 d-block">閲覧制限</label>
                <select
                  className="form-select"
                  value={viewRestriction}
                  onChange={(e) => { setViewRestriction(e.target.value); setPage(1); }}
                >
                  <option value="all">全て</option>
                  <option value="none">制限なし</option>
                  <option value="limited">制限あり</option>
                </select>
              </div>

              {/* 開催期間 from-to */}
              <div>
                <label className="form-label mb-1 d-block">開催期間</label>
                <div className="d-flex gap-2">
                  <input
                    className="form-control"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  />
                  <span className="text-body-tertiary" style={{ lineHeight: '38px' }}>〜</span>
                  <input
                    className="form-control"
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  />
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-3 align-items-center">
              <button type="submit" className="btn btn-primary">検索</button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

              <div className="ms-xxl-auto d-flex gap-3 align-items-center">
                {/* 件数 */}
                <div className="d-flex align-items-center gap-2">
                  <span className="text-body-tertiary">件数</span>
                  <select
                    className="form-select"
                    style={{ width: 110 }}
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                <button className="btn btn-link text-body me-2 px-0" type="button" onClick={handleExportCsv}>
                  <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                </button>

                <button
                  className="btn btn-primary"
                  id="addBtn"
                  type="button"
                  onClick={() => navigate('/stamprally/new')}
                >
                  <span className="fas fa-plus me-2"></span>新規登録
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* 一覧ブロック */}
        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
          <div className="table-responsive scrollbar mx-n1 px-1">
            <table className="table fs-9 mb-0">
              <thead>
                <tr>
                  <th className="white-space-nowrap fs-9 align-middle ps-0" style={{ maxWidth: '20px', width: '18px' }} />

                  <th className="align-middle ps-3">ID</th>

                  <th
                    className="sort align-middle ps-3"
                    role="button"
                    onClick={() => { toggleSort('title'); setPage(1); }}
                    title="名称でソート"
                  >
                    名称
                    <span className="fas ms-1 text-muted fa-sort" />
                  </th>

                  <th
                    className="sort align-middle ps-3"
                    role="button"
                    onClick={() => { toggleSort('open'); setPage(1); }}
                    title="開催開始日でソート"
                  >
                    開催期間
                    <span className="fas ms-1 text-muted fa-sort" />
                  </th>

                  <th className="align-middle ps-3">交換期間</th>

                  <th
                    className="sort align-middle ps-3"
                    role="button"
                    onClick={() => { toggleSort('publish'); setPage(1); }}
                    title="公開状態でソート"
                  >
                    公開
                    <span className="fas ms-1 text-muted fa-sort" />
                  </th>

                  <th className="align-middle ps-3">閲覧制限</th>
                  <th className="align-middle ps-3">達成条件</th>
                  <th className="align-middle ps-3">特典タイプ</th>
                  <th className="align-middle ps-3">設定状況</th>

                  <th
                    className="sort align-middle ps-3"
                    role="button"
                    onClick={() => { toggleSort('updated'); setPage(1); }}
                    title="更新日時でソート"
                  >
                    更新日時
                    <span className="fas ms-1 text-muted fa-sort" />
                  </th>

                  <th className="align-middle ps-3">操作</th>
                </tr>
              </thead>

              <tbody className="list">
                {!loading && !error && items.map((it) => {
                  const holding = getHoldingStatus(it);
                  const exchange = getExchangeStatus(it);
                  const publish = getPublishState(it);
                  const updated = it?.updatedAt ?? it?.updated_at ?? it?.modifiedAt ?? it?.modified_at;

                  const holdingRange = `${fmtDate(it?.eventOpenDate ?? it?.holdingStartDate ?? it?.startDate)}–${fmtDate(it?.eventCloseDate ?? it?.holdingEndDate ?? it?.endDate)}`;
                  const exchangeRange = `${fmtDate(it?.exchangeStartDate ?? it?.rewardExchangeStartDate)}–${fmtDate(it?.exchangeEndDate ?? it?.rewardExchangeEndDate)}`;

                  return (
                    <tr key={it.stamprallyId}>
                      <td className="fs-9 align-middle" />

                      <td className="align-middle ps-3">
                        {it.stamprallyId}
                      </td>

                      <td className="align-middle ps-3">
                        <button
                          type="button"
                          className="btn btn-link px-0 text-start"
                          onClick={() => goDetail(it.stamprallyId)}
                          title="詳細（編集）へ"
                          style={{ textDecoration: 'none' }}
                        >
                          {it.stamprallyName ?? '-'}
                        </button>
                      </td>

                      <td className="align-middle ps-3">
                        <div className="d-flex flex-column gap-1">
                          <div>{holdingRange}</div>
                          <div className={badgeClass(holding.key)} style={{ width: 'fit-content' }}>
                            {holding.label}
                          </div>
                        </div>
                      </td>

                      <td className="align-middle ps-3">
                        <div className="d-flex flex-column gap-1">
                          <div>{exchangeRange}</div>
                          <div className={badgeClass(exchange.key)} style={{ width: 'fit-content' }}>
                            {exchange.label}
                          </div>
                        </div>
                      </td>

                      <td className="align-middle ps-3">
                        <span className={badgeClass(publish.key)}>{publish.label}</span>
                      </td>

                      <td className="align-middle ps-3">{getViewRestrictionLabel(it)}</td>
                      <td className="align-middle ps-3">{getAchievementLabel(it)}</td>
                      <td className="align-middle ps-3">{getRewardTypeLabel(it)}</td>
                      <td className="align-middle ps-3">{getConfigStatusLabel(it)}</td>

                      <td className="align-middle ps-3">{fmtDateTime(updated)}</td>

                      <td className="align-middle ps-3">
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => navigate(`/stamprally/${it.stamprallyId}`)}
                            type="button"
                          >
                            詳細
                          </button>

                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/stamprally/${it.stamprallyId}/edit`)}
                            type="button"
                          >
                            編集
                          </button>

                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleTogglePublish(it)}
                            type="button"
                            title="公開/非公開を切替"
                          >
                            公開切替
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {loading && (
                  <tr>
                    <td colSpan={12} className="py-4 text-center text-body-tertiary">
                      読み込み中...
                    </td>
                  </tr>
                )}

                {!loading && !error && items.length === 0 && (
                  <tr>
                    <td colSpan={12} className="py-4 text-center text-body-tertiary">
                      該当するスタンプラリーがありません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ページング */}
          <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
            <div className="col-auto d-flex align-items-center gap-3">
              <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
              </p>
            </div>

            <div className="col-auto d-flex align-items-center">
              <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                <span className="fas fa-chevron-left" />
              </button>

              <ul className="mb-0 pagination">
                {pages.map((p) => (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button type="button" className="page-link" onClick={() => setPage(p)}>
                      {p}
                    </button>
                  </li>
                ))}
              </ul>

              <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                <span className="fas fa-chevron-right" />
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </div>

      {/* バリデーションエラーモーダル */}
      {validationModalOpen && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block', zIndex: 1055 }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg" style={{ border: '2px solid #ffc107' }}>
                <div className="modal-header" style={{ backgroundColor: '#ffc107' }}>
                  <h5 className="modal-title d-flex align-items-center fw-bold">
                    <span className="fas fa-exclamation-circle me-2" style={{ color: '#856404' }} />
                    <span style={{ color: '#856404' }}>公開できません</span>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setValidationModalOpen(false)}
                    style={{ filter: 'invert(0.5)' }}
                  />
                </div>
                <div className="modal-body" style={{ backgroundColor: '#fff' }}>
                  <p className="mb-3 text-dark">以下の設定が未完了のため、公開できません。</p>
                  <ul className="list-group">
                    {validationErrors.map((err, idx) => (
                      <li
                        key={idx}
                        className="list-group-item d-flex align-items-center"
                        style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}
                      >
                        <span className="fas fa-exclamation-triangle me-2" />
                        <span>{err.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="modal-footer" style={{ backgroundColor: '#f8f9fa' }}>
                  <button
                    type="button"
                    className="btn btn-warning fw-bold"
                    onClick={() => setValidationModalOpen(false)}
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 1050,
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
            onClick={() => setValidationModalOpen(false)}
          />
        </>
      )}
    </Layout>
  );
}
